import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../utils/userAuth';

interface TransmittalUser {
    id: number;
    name: string;
    email: string;
    branch?: string | null;
    permissions?: Array<{
        id: number;
        name: string;
        pivot?: {
            granted?: boolean;
        };
    }>;
    role?: {
        id: number;
        name: string;
    };
    requires_password_change?: boolean;
}

interface TransmittalAuthContextType {
    user: TransmittalUser | null;
    token: string | null;
    isAuthenticated: boolean;
    isTransmittalUser: boolean;
    isLoading: boolean;
    logout: () => void;
}

const TransmittalAuthContext = createContext<TransmittalAuthContextType | undefined>(undefined);

export const useTransmittalAuth = () => {
    const context = useContext(TransmittalAuthContext);
    if (!context) {
        throw new Error('useTransmittalAuth must be used within a TransmittalAuthProvider');
    }
    return context;
};

interface TransmittalAuthProviderProps {
    children: ReactNode;
}

export const TransmittalAuthProvider: React.FC<TransmittalAuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<TransmittalUser | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const handleForcedLogout = useCallback(() => {
        clearUserSession();
        setToken(null);
        setUser(null);
        navigate('/user/login');
    }, [navigate]);

    const normalizeToken = useCallback((value: unknown): string => String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, ''), []);

    const resolveBranchFromValue = useCallback((value: unknown): string | null => {
        const normalized = normalizeToken(value);
        if (!normalized) return null;
        if (normalized.includes('manila')) return 'Manila';
        if (normalized.includes('cebu')) return 'Cebu';
        if (normalized.includes('davao')) return 'Davao';
        if (normalized.includes('iloilo')) return 'Iloilo';
        if (normalized.includes('cagayan_de_oro') || normalized.includes('cdo')) return 'Cagayan de Oro';
        return null;
    }, [normalizeToken]);

    const isPermissionGranted = useCallback((rawGranted: unknown): boolean => {
        if (rawGranted === undefined || rawGranted === null) return true;
        if (typeof rawGranted === 'boolean') return rawGranted;
        if (typeof rawGranted === 'number') return rawGranted !== 0;
        if (typeof rawGranted === 'string') {
            const normalized = rawGranted.trim().toLowerCase();
            return !['0', 'false', 'no', 'off'].includes(normalized);
        }
        return Boolean(rawGranted);
    }, []);

    const getBranchFromPermissions = useCallback((permissions: TransmittalUser['permissions']) => {
        const grantedPermissionTokens = (permissions || [])
            .filter((permission) => {
                const rawGranted = (permission as any)?.granted ?? permission?.pivot?.granted;
                return isPermissionGranted(rawGranted);
            })
            .flatMap((permission: any) => [
                permission?.name,
                permission?.slug,
                permission?.code,
                permission?.key,
                permission?.permission_name,
            ])
            .filter(Boolean)
            .map(normalizeToken);

        const hasPermission = (predicate: (token: string) => boolean) =>
            grantedPermissionTokens.some(predicate);

        if (hasPermission((token) => token.includes('manila') && token.includes('transmittal'))) return 'Manila';
        if (hasPermission((token) => token.includes('cebu') && token.includes('transmittal'))) return 'Cebu';
        if (hasPermission((token) => token.includes('davao') && token.includes('transmittal'))) return 'Davao';
        if (hasPermission((token) => token.includes('iloilo') && token.includes('transmittal'))) return 'Iloilo';
        if (hasPermission((token) => (token.includes('cdo') || token.includes('cagayan_de_oro')) && token.includes('transmittal'))) return 'Cagayan de Oro';

        return null;
    }, [isPermissionGranted, normalizeToken]);

    const getBranchFromPayloadText = useCallback((payload: unknown): string | null => {
        const payloadText = normalizeToken(JSON.stringify(payload || {}));
        if (payloadText.includes('manila') && payloadText.includes('transmittal')) return 'Manila';
        if (payloadText.includes('cebu') && payloadText.includes('transmittal')) return 'Cebu';
        if (payloadText.includes('davao') && payloadText.includes('transmittal')) return 'Davao';
        if (payloadText.includes('iloilo') && payloadText.includes('transmittal')) return 'Iloilo';
        if ((payloadText.includes('cdo') || payloadText.includes('cagayan_de_oro')) && payloadText.includes('transmittal')) return 'Cagayan de Oro';
        return null;
    }, [normalizeToken]);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initAuth = () => {
            const storedToken = localStorage.getItem('userToken');
            const storedUserInfo = localStorage.getItem('userInfo');
            const storedTransmittalInfo = localStorage.getItem('transmittalInfo');

            if (storedToken && storedUserInfo) {
                try {
                    const userInfo = JSON.parse(storedUserInfo);
                    const transmittalInfo = storedTransmittalInfo ? JSON.parse(storedTransmittalInfo) : null;

                    // Verify that the user has transmittal role
                    const role = (userInfo?.role?.name || '').toLowerCase();
                    if (role === 'transmittal') {
                        const resolvedBranch =
                            resolveBranchFromValue(userInfo?.branch) ||
                            resolveBranchFromValue((userInfo as any)?.branch_name) ||
                            resolveBranchFromValue((userInfo as any)?.transmittal_branch) ||
                            resolveBranchFromValue((userInfo as any)?.clinic_location) ||
                            resolveBranchFromValue((userInfo as any)?.location) ||
                            resolveBranchFromValue(transmittalInfo?.branch) ||
                            getBranchFromPermissions(userInfo?.permissions) ||
                            getBranchFromPayloadText(userInfo);
                        if (!resolvedBranch) {
                            handleForcedLogout();
                            return;
                        }

                        setToken(storedToken);
                        setUser({
                            ...userInfo,
                            branch: resolvedBranch,
                        });
                    } else {
                        handleForcedLogout();
                    }
                } catch (error) {
                    handleForcedLogout();
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, [getBranchFromPayloadText, getBranchFromPermissions, handleForcedLogout, resolveBranchFromValue]);

    const logout = useCallback(() => {
        handleForcedLogout();
    }, [handleForcedLogout]);

    const isTransmittalUser = (user?.role?.name || '').toLowerCase() === 'transmittal';
    const isAuthenticated = !!token && !!user && isTransmittalUser;

    return (
        <TransmittalAuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated,
                isTransmittalUser,
                isLoading,
                logout,
            }}
        >
            {children}
        </TransmittalAuthContext.Provider>
    );
};
